/* eslint-disable promise/prefer-await-to-then */
/* eslint-disable promise/always-return */
import { ShopConfigData } from '@/core/apis/config_api/config_api_types';
import { CURR_ENV } from '@/core/constants/appConfig/environment';
import { TEventInputData } from '@/core/constants/event_types/additional_types';
import { EMableEventName } from '@/core/constants/event_types/events';
import { MarketDataHandler } from '@/core/data_handlers/market';
import { UniqueIdUtils } from '@/core/event_logic/utils/unique_id_utils';
import EventWriterServiceApi from '@/destinations/bundles/ews/event_writer_service_api';
import { getSellingPlans } from '@/shared/__ui_helper';
import { Logger } from '@/shared/logger/logger';
import { EBundleName, ESourceType, ISourceBundle } from '@/sources';
import {
  cpxPixelNameToEventNameMapping,
  cpxStandardEventNames,
  ExcludedCustomPixelEventsList,
  SupportedCustomPixelEvents,
} from '@/sources/bundles/wtag-shpfy-cpx/shopify_custom_pixel_types/cpx_event_name_types';
import {
  CpxLineItem,
  CustomPixelStdEventName,
  ShpyCpxHandlerInput,
} from '@/sources/bundles/wtag-shpfy-cpx/shopify_custom_pixel_types/cpx_payload_types';
import { convertCpxTimestamp } from '@/sources/bundles/wtag-shpfy-cpx/utils/timestamp';

import { ShopifyCustomPixelEventTriggerMap } from './event_triggers/event_handler_map';
import { GenericShpyCpxTrigger } from './event_triggers/generic_shpy_cpx_event_triggers';
import { ShpyCpxFallBackTrigger } from './event_triggers/shpy_cpx_event_triggers';
import {
  CartProcessingService,
  PRODUCT_ADDED_GENERATED_TAG,
} from './services/cart_processing_service';
import { updateShopifyMarketData } from './shopify_markets';

import { EScriptEnv } from '../../../core/constants/appConfig/types';
import { ShopifyCartTokenSync } from '../wtag-shpfy/token_syncing/sync_cart_token';

const getEventQueueHandlerName = (): string =>
  CURR_ENV === EScriptEnv.PROD ? 'wtag_q' : `wtag_${CURR_ENV}_q`;

export class ShopifyCustomPixelSourceBundle implements ISourceBundle {
  type = ESourceType.SHOPIFY;
  name = EBundleName.SHOPIFY_CUSTOM_PIXEL;
  sourceInitConfig: any;
  cpx_handler_window_func_name = getEventQueueHandlerName();

  static wtagFunction: (...args: any[]) => void;
  static cpx_event_handler_map: Record<
    SupportedCustomPixelEvents,
    GenericShpyCpxTrigger
  > = ShopifyCustomPixelEventTriggerMap;

  constructor(sourceInitConfig?: any, wtagFunction?: (...args: any[]) => void) {
    if (!wtagFunction) {
      throw new Error(
        'wtagFunction is required for ShopifyCustomPixelSourceBundle',
      );
    }
    this.sourceInitConfig = sourceInitConfig;
    ShopifyCustomPixelSourceBundle.wtagFunction = wtagFunction;
  }

  sourceInitCallbackFunction(
    config: ShopConfigData,
    _aid: string,
    sid: string,
  ) {
    if (!config.active) return;
    if (!config.config?.shpy_cartTokenSyncDisabled && sid) {
      void ShopifyCartTokenSync.syncCartAndCheckoutToken(sid);
    }
    // Fetch and update market data
  }

  sourceMarketInitCallbackFunction(config: ShopConfigData) {
    if (!config.active) return;
    // Fetch and update market data
    MarketDataHandler.setShopMarketData(config?.shopifyMarkets || []);
    // Update the market data if required
    updateShopifyMarketData();
  }

  init(): void {
    ShopifyCustomPixelSourceBundle.wtagFunction(
      'init',
      {},
      this.sourceInitCallbackFunction.bind(this),
      this.sourceMarketInitCallbackFunction.bind(this),
    );
    (window as any)[this.cpx_handler_window_func_name] =
      this.handleCustomPixelEvent.bind(this);
  }

  static async fetchWtagInputAndName(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): Promise<{
    mable_event_name: EMableEventName;
    wtag_input: TEventInputData;
  }> {
    const {
      event: { name: event_name },
    } = cpx_input_payload;
    const event_trigger =
      this.cpx_event_handler_map[event_name as SupportedCustomPixelEvents] ||
      ShpyCpxFallBackTrigger;
    const wtag_input =
      await event_trigger.transformToWtagInput(cpx_input_payload);
    const mable_event_name =
      cpxPixelNameToEventNameMapping[
        event_name as SupportedCustomPixelEvents
      ] ||
      event_name ||
      'no_event_name';
    Logger.info(`Custom Pixel Event Fired ${mable_event_name}`, wtag_input);
    return { mable_event_name, wtag_input };
  }

  extractTagsFromCheckoutCompletedEvent(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): string[] {
    const tags: string[] = [];
    const lineItems: CpxLineItem[] =
      cpx_input_payload.event.data.checkout?.lineItems || [];

    for (const item of lineItems) {
      if (item.sellingPlanAllocation?.sellingPlan) {
        tags.push('Subscription First Order');
        break;
      }
    }

    // Use the getSellingPlans function from __ui_helper.ts
    const sellingPlans = getSellingPlans();
    if (sellingPlans.length > 0) {
      Logger.info('Has Selling Plan', sellingPlans);
      tags.push('Has Selling Plan');
    }

    return tags;
  }

  async handleCustomPixelEvent(cpx_input_payload: ShpyCpxHandlerInput) {
    const {
      event: { name: event_name },
    } = cpx_input_payload;

    if (this.shouldSkipEvent(event_name)) {
      // process product removed events just for cart updates
      if (event_name == CustomPixelStdEventName.PRODUCT_REMOVED_FROM_CART)
        CartProcessingService.calcAddToCartEventsFromCpxInput(
          cpx_input_payload,
        );
      return;
    }

    // Process additional tags for special events
    const additionalTags = this.getAdditionalTagsForEvent(
      event_name,
      cpx_input_payload,
    );

    // Get Mable event name and WTAG input data
    const { mable_event_name, wtag_input } =
      await ShopifyCustomPixelSourceBundle.fetchWtagInputAndName(
        cpx_input_payload,
      );

    // Calculate additional ATC events from cart data
    const atcWtagEventInputs =
      CartProcessingService.calcAddToCartEventsFromCpxInput(cpx_input_payload);
    const all_wtag_inputs = [wtag_input].concat(atcWtagEventInputs);
    Logger.debug('Final event count: ' + all_wtag_inputs.length);

    // Enrich event data with additional info
    this.enrichEventData(all_wtag_inputs, cpx_input_payload, additionalTags);

    // Send events to both Event Writer Service and WTAG
    await this.sendEvents(mable_event_name, all_wtag_inputs, cpx_input_payload);
  }

  shouldSkipEvent(event_name: string): boolean {
    // Skip excluded events
    if (ExcludedCustomPixelEventsList.includes(event_name as any)) {
      Logger.warn(`Event name ${event_name} not supported`);
      return true;
    }

    // Check if it's a standard event
    if (!cpxStandardEventNames.includes(event_name as any)) {
      return true;
    }

    return false;
  }

  getAdditionalTagsForEvent(
    event_name: CustomPixelStdEventName | string,
    cpx_input_payload: ShpyCpxHandlerInput,
  ): string[] {
    // Add special handling for checkout completed event
    if (event_name === CustomPixelStdEventName.CHECKOUT_COMPLETED) {
      return this.extractTagsFromCheckoutCompletedEvent(cpx_input_payload);
    }
    return [];
  }

  enrichEventData(
    wtag_inputs: TEventInputData[],
    cpx_input_payload: ShpyCpxHandlerInput,
    additionalTags: string[],
  ): void {
    for (const wtag_input of wtag_inputs) {
      // Ensure eventData exists
      if (!wtag_input.eventData) {
        wtag_input.eventData = {};
      }

      // Add tags if they exist
      if (additionalTags.length > 0) {
        wtag_input.eventData.tags = [
          ...(wtag_input.eventData.tags || []),
          ...additionalTags,
        ];
      }

      // Add CPX metadata
      if (!wtag_input.eventData.cpx_timestamp) {
        // Use the utility function to convert the timestamp
        const { eventTimestamp } = convertCpxTimestamp(
          cpx_input_payload.event.timestamp,
        );
        if (eventTimestamp) {
          wtag_input.eventData.cpx_timestamp = eventTimestamp;
        }
      }

      if (!wtag_input.eventData.cpx_event_id) {
        wtag_input.eventData.cpx_event_id = cpx_input_payload.event.id;
      }
      if (!wtag_input.eventData.cpx_timestamp) {
        wtag_input.eventData.cpx_timestamp = Number(
          cpx_input_payload.event.timestamp,
        );
      }

      if (!wtag_input.eventData.eventID) {
        wtag_input.eventData.eventID = UniqueIdUtils.getUuidV7();
      }
    }
  }

  async sendEvents(
    mable_event_name: string,
    wtag_inputs: TEventInputData[],
    cpx_input_payload: ShpyCpxHandlerInput,
  ): Promise<void> {
    const main_wtag_input = wtag_inputs[0];
    if (!main_wtag_input) return;

    // Prepare the custom pixel payload
    const customPixelPayload = {
      eventId: main_wtag_input.eventData?.eventID || '',
      eventName: mable_event_name,
      inputEventId: main_wtag_input.eventData?.cpx_event_id || '',
      inputEventName: cpx_input_payload.event.name || '',
      inputEventTimestamp:
        main_wtag_input.eventData?.cpx_timestamp?.toString() || '',
      rawCpxPayload: cpx_input_payload,
      sourceType: 'shopifyCustomPixel' as const,
      sourceUid: cpx_input_payload.event.clientId || '',
    };

    try {
      // Send both events in parallel
      await Promise.all([
        // Send event to Event Writer Service
        EventWriterServiceApi.sendCustomPixelEvent(customPixelPayload).catch(
          error => {
            Logger.error(
              'Failed to send events to Event Writer Service',
              error,
            );
          },
        ),
        // Send event to WTAG
        ...wtag_inputs.map(wtag_input => {
          // default to main wtag mable event name
          let mableEventName = mable_event_name;

          // if generated tag is found, it is a generated Product added event
          if (wtag_input.eventData?.tags?.includes(PRODUCT_ADDED_GENERATED_TAG))
            mableEventName = EMableEventName.PRODUCT_ADDED;

          return Promise.resolve(
            ShopifyCustomPixelSourceBundle.wtagFunction(
              'event',
              mableEventName,
              wtag_input,
            ),
          );
        }),
      ]);
    } catch (error) {
      Logger.error('Failed to process events', error);
    }
  }
}
